"use client";

import type { ComponentProps, ReactNode, MouseEventHandler } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PermissionWrapper } from "../permissao/PermissionWrapper";
import { cn } from "@/lib/utils";
import { PermissaoEnum } from "@workflow/types";

interface TopBarSectionProps {
  // Dados
  entity?: string;
  entityBadge?: ReactNode;
  title: string;
  subtitle?: ReactNode;
  headingLevel?: "h1" | "h2" | "h3" | "h4";
  className?: string;
  loading?: boolean;

  // Botão Principal
  showButton?: boolean;
  disabledButton?: boolean;
  buttonLabel?: string;
  buttonIcon?: ReactNode;
  buttonVariant?: ComponentProps<typeof Button>["variant"];
  onButtonClick?: MouseEventHandler<HTMLButtonElement>;
  buttonPermission?: PermissaoEnum;
  buttonPermissions?: PermissaoEnum[];
  requireAllButtonPermissions?: boolean;

  // Ações
  extraActions?: ReactNode;
  extraActionsPermission?: PermissaoEnum;
  extraActionsPermissions?: PermissaoEnum[];
  requireAllExtraActionsPermissions?: boolean;

  dropdownActions?: ReactNode;
  dropdownActionsPermission?: PermissaoEnum;
  dropdownActionsPermissions?: PermissaoEnum[];
  requireAllDropdownActionsPermissions?: boolean;
}

export function TopBarSection({
  entity,
  entityBadge,
  title,
  subtitle,
  headingLevel = "h1",
  className,
  loading = false,
  showButton = false,
  disabledButton = false,
  buttonLabel,
  buttonIcon,
  buttonVariant,
  onButtonClick,
  buttonPermission,
  buttonPermissions,
  requireAllButtonPermissions = false,
  extraActions,
  extraActionsPermission,
  extraActionsPermissions,
  requireAllExtraActionsPermissions = false,
  dropdownActions,
  dropdownActionsPermission,
  dropdownActionsPermissions,
  requireAllDropdownActionsPermissions = false,
}: TopBarSectionProps) {
  const HeadingTag = headingLevel;
  const hasActions = extraActions || dropdownActions || showButton;

  if (loading) return <TopBarSkeleton />;

  return (
    <div
      className={cn(
        "flex flex-col gap-3 border-b border-border pb-5",
        "sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      {/* TÍTULO */}
      <div className="min-w-0 flex-1 space-y-1">
        {(entity || entityBadge) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {entity && (
              <span className="inline-flex select-none items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60">
                <span className="inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-primary/40" />
                {entity}
              </span>
            )}
            {entityBadge && (
              <>
                {entity && <span className="select-none text-xs leading-none text-border">·</span>}
                {typeof entityBadge === "string" ? (
                  <span className="inline-flex items-center rounded bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-secondary-foreground ring-1 ring-inset ring-border/50">
                    {entityBadge}
                  </span>
                ) : (
                  entityBadge
                )}
              </>
            )}
          </div>
        )}

        <HeadingTag
          className="scroll-m-20 truncate font-display text-2xl font-semibold tracking-tight text-foreground"
          title={title}
        >
          {title}
        </HeadingTag>

        {subtitle && <p className="text-sm leading-snug text-muted-foreground">{subtitle}</p>}
      </div>

      {/* AÇÕES */}
      {hasActions && (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <PermissionWrapper
            permission={extraActionsPermission}
            permissions={extraActionsPermissions}
            requireAll={requireAllExtraActionsPermissions}
          >
            {extraActions}
          </PermissionWrapper>

          <PermissionWrapper
            permission={dropdownActionsPermission}
            permissions={dropdownActionsPermissions}
            requireAll={requireAllDropdownActionsPermissions}
          >
            {dropdownActions}
          </PermissionWrapper>

          <PermissionWrapper
            permission={buttonPermission}
            permissions={buttonPermissions}
            requireAll={requireAllButtonPermissions}
          >
            {showButton && (
              <Button
                onClick={onButtonClick}
                size="sm"
                variant={buttonVariant}
                aria-label={buttonLabel || "Ação principal"}
                disabled={disabledButton}
                className={cn("gap-2 font-medium", !buttonLabel && "aspect-square px-2")}
              >
                {buttonIcon}
                {buttonLabel}
              </Button>
            )}
          </PermissionWrapper>
        </div>
      )}
    </div>
  );
}

function TopBarSkeleton() {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 border-b border-border pb-5",
        "sm:flex-row sm:items-center sm:justify-between",
        "animate-pulse",
      )}
    >
      <div className="flex-1 space-y-2">
        <Skeleton className="h-[11px] w-16 rounded" />
        <Skeleton className="h-7 w-52 rounded" />
        <Skeleton className="h-4 w-72 rounded" />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Skeleton className="h-8 w-24 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  );
}
